import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, TIER_LABEL } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSEO from "../lib/useSEO";
import { Loader2, X, ShoppingCart, MapPin, KeyRound, Inbox, UserCircle2 } from "lucide-react";
import ChangePasswordModal from "../components/ChangePasswordModal";
import AttorneyLeadsTab from "../components/AttorneyLeadsTab";
import AttorneyProfileForm from "../components/AttorneyProfileForm";

export default function AttorneyDashboardPage() {
    const { user, loading: authLoading } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") || "profile";
    const [tab, setTab] = useState(initialTab);
    // Keep the URL `?tab=` query in sync so deep-linked emails / mobile menu
    // entries land on the right pane (and back/forward still work).
    useEffect(() => {
        const cur = searchParams.get("tab");
        if (cur !== tab) {
            const np = new URLSearchParams(searchParams);
            np.set("tab", tab);
            setSearchParams(np, { replace: true });
        }
    }, [tab]); // eslint-disable-line
    const [newLeadCount, setNewLeadCount] = useState(0);
    const [profile, setProfile] = useState(null);
    const [placements, setPlacements] = useState([]);
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [counties, setCounties] = useState([]);
    const [filterState, setFilterState] = useState("");
    const [countySearch, setCountySearch] = useState("");
    const [showBuy, setShowBuy] = useState(false);
    const [buy, setBuy] = useState({ category_id: "", county_id: "", tier: "basic" });
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [showPwModal, setShowPwModal] = useState(false);
    const [paymentsConfig, setPaymentsConfig] = useState({ mock_mode: true });
    const [purchaseErr, setPurchaseErr] = useState("");
    // Referral attribution surfaced inside the buy modal.
    const [referrer, setReferrer] = useState(null);  // { has_referrer, referral_code, affiliate_name } | null
    const [refInput, setRefInput] = useState("");
    const [refStatus, setRefStatus] = useState("");  // "" | "applying" | "applied" | "error"
    const [refError, setRefError] = useState("");

    useSEO({ title: "Attorney Dashboard", path: "/attorney/dashboard", noindex: true });

    useEffect(() => {
        api.get("/payments/config").then(({ data }) => setPaymentsConfig(data)).catch(() => {});
    }, []);

    // When the buy modal opens, fetch the attached referrer (if any).
    // If none is attached, pre-fill the input from /join?ref= sessionStorage.
    useEffect(() => {
        if (!showBuy) return;
        let cancelled = false;
        setRefError(""); setRefStatus("");
        api.get("/attorney/referrer").then(({ data }) => {
            if (cancelled) return;
            setReferrer(data);
            if (!data.has_referrer) {
                try { setRefInput((sessionStorage.getItem("ll_ref") || "").toUpperCase()); } catch { /* ignore */ }
            } else {
                setRefInput("");
            }
        }).catch(() => { if (!cancelled) setReferrer(null); });
        return () => { cancelled = true; };
    }, [showBuy]);

    const applyRefCode = async () => {
        const code = (refInput || "").trim().toUpperCase();
        if (!code) return;
        setRefStatus("applying"); setRefError("");
        try {
            const { data } = await api.post("/attorney/attach-referrer", { ref: code });
            setReferrer({
                has_referrer: true,
                referral_code: data.referral_code,
                affiliate_name: data.affiliate_name,
            });
            setRefStatus("applied");
            try { sessionStorage.removeItem("ll_ref"); } catch { /* ignore */ }
        } catch (e) {
            setRefError(e?.response?.data?.detail || "Couldn't apply that code.");
            setRefStatus("error");
        }
    };

    // Poll for new lead count so the tab badge stays fresh.
    useEffect(() => {
        let cancelled = false;
        const fetchCount = () => api.get("/attorney/leads?status_filter=new")
            .then(({ data }) => { if (!cancelled) setNewLeadCount(data?.counts?.new || 0); })
            .catch(() => {});
        fetchCount();
        const t = setInterval(fetchCount, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    // Stripe redirects back here with ?checkout=success|cancel after payment.
    // Surface a toast and clear the param from the URL so it doesn't fire twice.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const result = params.get("checkout");
        if (result === "success") {
            toast.success("Payment received — your placement is being activated.", {
                description: "It'll move from Pending to Active as soon as Stripe confirms (usually within a few seconds).",
            });
        } else if (result === "cancel") {
            toast.info("Checkout cancelled. Nothing was charged.");
        }
        if (result) {
            params.delete("checkout");
            const qs = params.toString();
            window.history.replaceState({}, "", `${window.location.pathname}${qs ? "?" + qs : ""}`);
        }
    }, []);

    useEffect(() => {
        if (!user || user.role !== "attorney") return;
        Promise.all([
            api.get("/attorney/profile"),
            api.get("/attorney/placements"),
            api.get("/categories"),
            api.get("/states"),
        ]).then(([p, pl, c, s]) => {
            setProfile(p.data); setPlacements(pl.data);
            setCategories(c.data); setStates(s.data);
            if (c.data.length) setBuy(b => ({...b, category_id: c.data[0].id}));
        });
    }, [user]);

    useEffect(() => {
        if (!filterState) { setCounties([]); return; }
        api.get("/counties", { params: { state_code: filterState } }).then(({data}) => {
            setCounties(data);
            setCountySearch("");
            setBuy(b => ({...b, county_id: ""}));
        });
    }, [filterState]);

    const filteredCounties = useMemo(() => {
        if (!countySearch.trim()) return counties.slice(0, 50);
        const q = countySearch.toLowerCase();
        return counties.filter(c => c.name.toLowerCase().includes(q)).slice(0, 50);
    }, [counties, countySearch]);

    const selectedCounty = counties.find(c => c.id === buy.county_id);

    useEffect(() => {
        if (!buy.category_id || !buy.county_id) { setQuote(null); return; }
        api.get("/pricing/quote", { params: { category_id: buy.category_id, county_id: buy.county_id } })
            .then(({data}) => setQuote(data))
            .catch(() => setQuote(null));
    }, [buy.category_id, buy.county_id]);

    const tierPrice = useMemo(() => {
        if (!quote) return 0;
        return quote.pricing[`${buy.tier}_price`] ?? 0;
    }, [quote, buy.tier]);

    if (authLoading) {
        return (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-sm text-muted-foreground" data-testid="attorney-dashboard-loading">Loading…</div>
            </section>
        );
    }
    if (!user) return <Navigate to="/attorney/auth" replace />;
    if (user.role === "admin") return <Navigate to="/admin/dashboard" replace />;

    const purchase = async (testMode = false) => {
        setBusy(true); setMsg(""); setPurchaseErr("");
        try {
            // Include any typed-but-not-yet-applied ref code as a final fallback —
            // the backend will only honor it if no referrer is attached yet.
            const fallbackRef = (!referrer?.has_referrer && refInput.trim()) ? refInput.trim().toUpperCase() : undefined;
            const { data } = await api.post("/attorney/placements", { ...buy, test_mode: testMode, ref: fallbackRef });
            // Live Stripe path: redirect to checkout
            if (data.checkout_url) {
                window.location.assign(data.checkout_url);
                return;
            }
            // Mock path: placement is already active
            const statusLabel = data.placement_status === "active" ? "active" : data.placement_status || "ok";
            const checkoutNote = data.mock ? "mocked checkout" : statusLabel;
            setMsg(`✓ Placement ${data.status} at ${TIER_LABEL[data.tier]} — $${data.price}/mo (${checkoutNote})`);
            const { data: pls } = await api.get("/attorney/placements");
            setPlacements(pls);
            setShowBuy(false);
        } catch (e) {
            setPurchaseErr(e?.response?.data?.detail || e?.message || "Could not complete purchase.");
        } finally { setBusy(false); }
    };

    const cancel = async (id) => {
        if (!window.confirm("Cancel this placement?")) return;
        await api.delete(`/attorney/placements/${id}`);
        const { data: pls } = await api.get("/attorney/placements");
        setPlacements(pls);
    };

    const activeCount = placements.filter(p => p.status === "active").length;

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="ll-label mb-2">Attorney Dashboard</div>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
                <div>
                    <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight" data-testid="dashboard-title">
                        Welcome, {profile?.user?.name || "Counselor"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">{profile?.profile?.firm_name || "Independent practice"} · {profile?.user?.email}</p>
                </div>
                <button onClick={() => setShowPwModal(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2 text-xs font-semibold hover:bg-accent"
                        data-testid="open-pw-modal-btn">
                    <KeyRound className="h-3.5 w-3.5" /> {user.has_password === false ? "Set password" : "Change password"}
                </button>
            </div>
            <div className="mb-8" />

            <ChangePasswordModal open={showPwModal} onClose={() => setShowPwModal(false)} isGoogleOnly={user.has_password === false} />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="ll-card">
                    <div className="ll-label mb-1">Active placements</div>
                    <div className="font-serif text-3xl font-bold">{activeCount}</div>
                </div>
                <div className="ll-card">
                    <div className="ll-label mb-1">Counties covered</div>
                    <div className="font-serif text-3xl font-bold">{new Set(placements.filter(p=>p.status==="active").map(p=>p.county_id)).size}</div>
                </div>
                <div className="ll-card">
                    <div className="ll-label mb-1">Practice areas</div>
                    <div className="font-serif text-3xl font-bold">{new Set(placements.filter(p=>p.status==="active").map(p=>p.category_id)).size}</div>
                </div>
            </div>

            <div className="flex items-center gap-1 border-b border-border mb-6 overflow-x-auto" data-testid="attorney-tabs">
                <button onClick={() => setTab("profile")}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                            tab === "profile" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid="attorney-tab-profile">
                    <UserCircle2 className="h-4 w-4" />
                    Profile
                </button>
                <button onClick={() => setTab("placements")}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                            tab === "placements" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid="attorney-tab-placements">
                    Placements
                </button>
                <button onClick={() => setTab("leads")}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                            tab === "leads" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid="attorney-tab-leads">
                    <Inbox className="h-4 w-4" />
                    Leads
                    {newLeadCount > 0 && (
                        <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-2 py-0.5 tabular-nums" data-testid="leads-badge">
                            {newLeadCount}
                        </span>
                    )}
                </button>
            </div>

            {tab === "profile" && <AttorneyProfileForm userId={user?.id} userName={user?.name} />}
            {tab === "leads" && <AttorneyLeadsTab />}

            {tab === "placements" && (<>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl font-semibold">Your placements</h2>
                <button onClick={() => setShowBuy(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90"
                        data-testid="buy-placement-btn">
                    <ShoppingCart className="h-4 w-4" /> Buy placement
                </button>
            </div>

            {msg && <div className="mb-4 ll-card text-sm bg-accent/40" data-testid="dashboard-msg">{msg}</div>}

            {placements.length === 0 ? (
                <div className="ll-card text-sm text-muted-foreground" data-testid="no-placements">
                    No placements yet. Buy your first county to start appearing in consumer results.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="placement-list">
                    {placements.map((p) => (
                        <div key={p.id} className="ll-card" data-testid={`placement-${p.id}`}>
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <div className="font-serif text-lg font-semibold">{p.category?.name}</div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {p.county?.name}, {p.county?.state_code}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {p.is_test && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300" data-testid={`test-badge-${p.id}`}>
                                            Test
                                        </span>
                                    )}
                                    <span className={`ll-pill tier-${p.tier}`}>{TIER_LABEL[p.tier]}</span>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground mb-3">Status: <span className="font-semibold capitalize text-foreground">{p.status}</span></div>
                            {p.status === "active" && (
                                <button onClick={() => cancel(p.id)}
                                        className="text-xs font-medium text-destructive hover:underline"
                                        data-testid={`cancel-placement-${p.id}`}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            </>)}

            {/* BUY MODAL */}
            {showBuy && (
                <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowBuy(false)}>
                    <div className="bg-card rounded-lg shadow-xl max-w-lg w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="buy-modal">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <div className="ll-label mb-1">Purchase Placement</div>
                                <h3 className="font-serif text-2xl font-bold">Buy visibility</h3>
                            </div>
                            <button onClick={() => setShowBuy(false)} className="h-8 w-8 rounded-full hover:bg-accent flex items-center justify-center" data-testid="close-buy-modal">
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {purchaseErr && (
                                <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-sm p-3" role="alert" data-testid="purchase-error">
                                    <div className="font-semibold mb-1">Payment couldn't start</div>
                                    <div className="text-xs leading-relaxed">{purchaseErr}</div>
                                </div>
                            )}
                            <div>
                                <label className="ll-label block mb-1.5">Practice area</label>
                                <select className="ll-input" value={buy.category_id}
                                        onChange={e => setBuy({...buy, category_id: e.target.value})}
                                        data-testid="buy-category-select">
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="ll-label block mb-1.5">State</label>
                                    <select className="ll-input" value={filterState}
                                            onChange={e => setFilterState(e.target.value)}
                                            data-testid="buy-state-select">
                                        <option value="">Choose…</option>
                                        {states.map(s => (
                                            <option key={s.code} value={s.code}>
                                                {s.is_sandbox ? `${s.name}` : s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="ll-label block mb-1.5">
                                        County {counties.length > 0 && <span className="text-muted-foreground font-normal">({counties.length} in state)</span>}
                                    </label>
                                    <input
                                        className="ll-input"
                                        disabled={!counties.length}
                                        placeholder={counties.length ? "Type to search…" : "Choose a state first"}
                                        value={countySearch}
                                        onChange={e => setCountySearch(e.target.value)}
                                        data-testid="buy-county-search"
                                    />
                                </div>
                            </div>

                            {counties.length > 0 && (
                                <div className="max-h-44 overflow-y-auto border border-border rounded-md bg-card divide-y divide-border" data-testid="buy-county-list">
                                    {filteredCounties.map(c => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => setBuy({...buy, county_id: c.id})}
                                            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-accent flex items-center justify-between gap-2 transition ${
                                                buy.county_id === c.id ? "bg-accent" : ""
                                            }`}
                                            data-testid={`buy-county-option-${c.id}`}>
                                            <span className="truncate">
                                                {c.name}
                                                <span className="ml-2 text-xs text-muted-foreground tabular-nums">
                                                    {c.population ? c.population.toLocaleString() : ""} pop.
                                                </span>
                                            </span>
                                            <span className={`ll-pill text-[10px] px-2 py-0.5 ${
                                                c.demand_band === "A" ? "tier-top_spot" :
                                                c.demand_band === "B" ? "tier-featured" : ""
                                            }`}>Band {c.demand_band}</span>
                                        </button>
                                    ))}
                                    {filteredCounties.length === 0 && (
                                        <div className="px-3 py-3 text-xs text-muted-foreground">No counties match "{countySearch}"</div>
                                    )}
                                    {countySearch === "" && counties.length > 50 && (
                                        <div className="px-3 py-2 text-[11px] text-muted-foreground bg-accent/30">Showing first 50 — type to search the remaining {counties.length - 50}.</div>
                                    )}
                                </div>
                            )}

                            {selectedCounty && (
                                <div className="text-xs text-muted-foreground" data-testid="selected-county-info">
                                    Selected: <span className="font-semibold text-foreground">{selectedCounty.name}, {selectedCounty.state_code}</span>
                                    {selectedCounty.population ? ` · pop. ${selectedCounty.population.toLocaleString()}` : ""}
                                    {" "}· Band {selectedCounty.demand_band}
                                </div>
                            )}
                            <div>
                                <label className="ll-label block mb-1.5">Tier</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {["basic", "featured", "top_spot"].map(t => {
                                        const disabled = t === "top_spot" && quote && quote.top_spot_available === 0;
                                        return (
                                            <button key={t} type="button" disabled={disabled}
                                                    onClick={() => setBuy({...buy, tier: t})}
                                                    className={`p-3 rounded-md border text-sm font-semibold transition ${
                                                        buy.tier === t ? "border-primary bg-accent" : "border-border hover:bg-accent/50"
                                                    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                                                    data-testid={`buy-tier-${t}`}>
                                                {TIER_LABEL[t]}
                                                {t === "top_spot" && quote && (
                                                    <div className="text-xs font-normal text-muted-foreground mt-0.5">
                                                        {quote.top_spot_available} / {quote.top_spot_capacity} left
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {quote && (
                                <div className="p-4 rounded-md bg-accent/40 border border-border" data-testid="buy-quote-panel">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className="ll-label">Monthly price</span>
                                        <span className="font-serif font-bold text-3xl text-primary" data-testid="buy-quote-price">${tierPrice}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Band {quote.band} · Tier: {TIER_LABEL[buy.tier]} · 30-day placement{paymentsConfig.mock_mode ? " (mocked checkout)" : ""}
                                    </div>
                                </div>
                            )}

                            {/* REFERRAL ATTRIBUTION — visible in modal so it's verifiable before checkout */}
                            <div className="rounded-md border border-border bg-muted/20 p-3" data-testid="buy-referral-block">
                                {referrer?.has_referrer ? (
                                    <div className="space-y-1" data-testid="referral-applied">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="ll-label">Referral code applied</span>
                                            <code className="font-mono text-sm font-bold text-primary tabular-nums" data-testid="referral-code-locked">{referrer.referral_code}</code>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground leading-snug">
                                            Commission will be credited to <strong>{referrer.affiliate_name}</strong> after successful payment and the 30-day hold period. Referrals are first-touch and locked to this account.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2" data-testid="referral-input-block">
                                        <label className="ll-label block">Have a referral code? <span className="text-muted-foreground/70 font-normal">(optional)</span></label>
                                        <div className="flex gap-2">
                                            <input
                                                value={refInput}
                                                onChange={(e) => setRefInput(e.target.value.toUpperCase())}
                                                placeholder="YBMXXXX"
                                                maxLength={16}
                                                className="ll-input font-mono uppercase tracking-wider flex-1 text-sm"
                                                data-testid="referral-code-input"
                                            />
                                            <button type="button" onClick={applyRefCode} disabled={refStatus === "applying" || !refInput.trim()}
                                                    className="rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold border border-primary/30 hover:bg-primary/15 disabled:opacity-50"
                                                    data-testid="referral-apply-btn">
                                                {refStatus === "applying" ? "Applying…" : "Apply"}
                                            </button>
                                        </div>
                                        {refError && <div className="text-xs text-destructive" data-testid="referral-error">{refError}</div>}
                                        <p className="text-[11px] text-muted-foreground leading-snug">
                                            If a partner referred you, paste their code so they receive credit. Once applied, it's locked to your account.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <button onClick={() => purchase(false)} disabled={busy || !quote}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 font-semibold hover:bg-primary/90 disabled:opacity-60"
                                    data-testid="confirm-purchase-btn">
                                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                                {paymentsConfig.mock_mode ? "Confirm purchase (mocked)" : "Continue to secure payment"}
                            </button>
                            {selectedCounty?.is_sandbox && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-relaxed" data-testid="sandbox-county-note">
                                    <strong>Sandbox county.</strong> This is a private $1 live-Stripe test placement. It will appear on your dashboard with a TEST badge and is invisible to real consumers (no ZIP maps to this county).
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
